import { useState, useCallback, useEffect, useRef, lazy, Suspense } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Phone, Mail, Menu, X, MessageSquare, Search, User, ChevronDown } from "lucide-react";
import logo from "@/assets/logo.webp";
import expertSupport from "@/assets/expert-support.webp";
import { categories } from "@/data/categories";
import { fetchSearchSuggestions, type SearchSuggestion } from "@/lib/shopify";
import { formatPrice } from "@/lib/utils";

const CartDrawer = lazy(() => import("@/components/CartDrawer").then(m => ({ default: m.CartDrawer })));
const CartIconMobile = lazy(() => import("@/components/CartDrawer").then(m => ({ default: m.CartIconMobile })));

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const transformerPartsLinks = [
  { to: "/transformer-bushings", label: "Transformer Bushings" },
  { to: "/buchholz-relay", label: "Buchholz Relay" },
  { to: "/transformer-oil-level-indicator", label: "Oil Level Indicators" },
  { to: "/transformer-insulators", label: "Insulators" },
  { to: "/disconnect-switches", label: "Disconnect Switches" },
];

const SiteHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobilePartsOpen, setMobilePartsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchMobileRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const scrollToQuoteForm = useCallback(() => {
    const scrollTo = () => {
      const el = document.getElementById("quote-form");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname === "/") {
      scrollTo();
    } else {
      navigate("/");
      setTimeout(scrollTo, 500);
    }
  }, [location.pathname, navigate]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      const results = await fetchSearchSuggestions(value.trim());
      setSuggestions(results);
      setShowSuggestions(true);
      setIsSearching(false);
    }, 300);
  };

  const handleSuggestionClick = (handle: string) => {
    setShowSuggestions(false);
    setSearchQuery("");
    navigate(`/product/${handle}`);
  };

  const handleRequestQuoteFromSearch = () => {
    setShowSuggestions(false);
    const query = searchQuery.trim();
    setSearchQuery("");
    navigate("/#quote-form", { state: { partNumber: query } });
    setTimeout(() => {
      const el = document.getElementById("quote-form");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 500);
  };

  const SuggestionsDropdown = () => (
    <>
      {suggestions.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => handleSuggestionClick(s.handle)}
          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors text-left border-b border-border last:border-0"
        >
          {s.imageUrl ? (
            <img src={s.imageUrl} alt={s.title} className="w-10 h-10 object-contain rounded border border-border flex-shrink-0" />
          ) : (
            <div className="w-10 h-10 bg-muted rounded border border-border flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{s.title}</p>
            <p className="text-xs text-muted-foreground">{s.vendor}</p>
          </div>
          <span className="text-sm font-bold text-brand flex-shrink-0">{formatPrice(s.price)}</span>
        </button>
      ))}
      {suggestions.length === 0 && !isSearching && searchQuery.trim().length >= 2 && (
        <div className="p-4 text-center">
          <p className="text-sm text-muted-foreground mb-2">
            No products found for "<span className="font-semibold text-foreground">{searchQuery.trim()}</span>"
          </p>
          <button
            type="button"
            onClick={handleRequestQuoteFromSearch}
            className="inline-flex items-center gap-2 bg-brand text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-brand/90 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            Request a Quote for "{searchQuery.trim()}"
          </button>
        </div>
      )}
      {isSearching && (
        <div className="p-3 text-center text-sm text-muted-foreground">Searching...</div>
      )}
    </>
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuggestions(false);
    if (searchQuery.trim()) {
      navigate(`/shop?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Close suggestions on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        searchRef.current && !searchRef.current.contains(e.target as Node) &&
        searchMobileRef.current && !searchMobileRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <header className="sticky top-0 z-50">
      {/* ── Row 1: Contact info (full width) ── */}
      <div className="hidden lg:block bg-[hsl(220,15%,13%)]">
        <div className="w-full px-4 xl:px-8 flex items-center justify-between h-9">
          <div className="flex items-center gap-5 text-[12px] text-white/70">
            <a href="tel:+18454533201" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5 text-white" />
              +1 (845) 453-3201
            </a>
            <a href="mailto:sales@nardyautomation.com" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Mail className="w-3.5 h-3.5 text-white" />
              <span translate="no">sales@nardyautomation.com</span>
            </a>
          </div>
          <div className="text-[13px] font-semibold text-white">
            Same Day Shipping &amp; Rush Processing Available
          </div>
          <div className="flex items-center gap-4 text-[12px] text-white/70">
            <a href="https://wa.me/18454533201" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <WhatsAppIcon className="w-3.5 h-3.5 text-[#25D366]" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* ── Row 2: Logo | Search | Account/Cart | Fast Quote ── */}
      <div className="hidden lg:block bg-background border-b border-border relative z-10">
        <div className="w-full px-4 xl:px-8 flex items-center gap-6 h-[72px]">
          <Link to="/" className="flex-shrink-0 relative z-20">
            <div className="bg-background rounded-b-xl shadow-[0_8px_20px_-4px_rgba(0,0,0,0.15)] border border-t-0 border-border/40 px-6 py-4" style={{ marginBottom: '-24px' }}>
              <img src={logo} alt="Nardy Automation" className="h-14 w-auto object-contain" width={210} height={63} />
            </div>
          </Link>

          <form onSubmit={handleSearch} className="flex-1 max-w-2xl mx-auto">
            <div className="relative" ref={searchRef}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Keyword / Part Number / SKU"
                className="w-full border border-border rounded-md py-2.5 pl-10 pr-24 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-colors"
                autoComplete="off"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <button
                type="submit"
                className="absolute right-1 top-1 bottom-1 px-5 bg-brand text-white text-sm font-semibold rounded-md hover:bg-brand/90 transition-colors"
              >
                Search
              </button>

              {/* Suggestions dropdown */}
              {showSuggestions && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                  <SuggestionsDropdown />
                </div>
              )}
            </div>
          </form>

          <div className="flex items-center gap-2">
            <Link to="/auth" className="flex items-center gap-1.5 border border-border rounded-md px-4 py-2 text-sm font-medium text-foreground/80 hover:text-brand hover:border-brand/40 transition-colors" aria-label="My Account">
              <User className="w-4 h-4" />
              My Account
            </Link>
            <Suspense fallback={null}><CartDrawer /></Suspense>
          </div>

          <button
            onClick={scrollToQuoteForm}
            className="flex-shrink-0 flex items-center gap-2 bg-brand text-white px-6 py-2.5 rounded-md text-sm font-bold hover:bg-brand/90 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            Fast Quote
          </button>
        </div>
      </div>

      {/* ── Row 3: Nav links | Expert support (overlaps into banner) ── */}
      <div className="hidden lg:block bg-background border-b border-border relative">
        <div className="w-full px-4 xl:px-8 flex items-center justify-between h-11">
          <nav className="flex items-center gap-7 text-[13px] font-medium text-foreground/80" style={{ paddingLeft: '320px' }}>
            <Link to="/" className="hover:text-brand transition-colors">Home</Link>
            <Link to="/shop" className="hover:text-brand transition-colors">Shop</Link>
            <Link to="/categories" className="hover:text-brand transition-colors">Categories</Link>

            {/* Transformer Parts dropdown */}
            <div className="relative group">
              <Link to="/transformer-parts" className="flex items-center gap-1 hover:text-brand transition-colors py-2">
                Transformer Parts
                <ChevronDown className="w-3 h-3" />
              </Link>
              <div className="absolute top-full left-0 pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                <div className="bg-background border border-border rounded-md shadow-lg py-2 min-w-[240px]">
                  <Link to="/transformer-parts" className="block px-4 py-2 text-sm font-semibold text-brand hover:bg-muted transition-colors border-b border-border">
                    All Transformer Parts →
                  </Link>
                  {transformerPartsLinks.map((l) => (
                    <Link key={l.to} to={l.to} className="block px-4 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-brand transition-colors">
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link to="/transformer-services" className="hover:text-brand transition-colors">Services</Link>
            <Link to="/industrial-automation-parts" className="hover:text-brand transition-colors">Automation Parts</Link>
            <Link to="/manufacturers" className="hover:text-brand transition-colors">Manufacturers</Link>
            <Link to="/about" className="hover:text-brand transition-colors">About</Link>
            <Link to="/contact" className="hover:text-brand transition-colors">Contact</Link>
            <Link to="/request-quote" className="bg-brand text-white px-3 py-1 rounded-md hover:bg-brand/90 transition-colors">Request a Quote</Link>
          </nav>
          <div className="flex items-center gap-2">
              <div className="w-10 h-10 overflow-hidden bg-muted flex-shrink-0">
                <img 
                  src={expertSupport} 
                  alt="Expert support agent" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex flex-col items-start leading-tight whitespace-nowrap">
                <a href="tel:+18454533201" className="text-[13px] font-bold text-brand hover:text-brand/80 transition-colors">
                  +1 (845) 453-3201
                </a>
                <span className="text-[10px] text-muted-foreground font-medium">
                  Experts Available 24/7 365
                </span>
              </div>
            </div>
        </div>
      </div>

      {/* ── Tablet (md only) ── */}
      <div className="hidden md:flex lg:hidden bg-background border-b border-border items-center justify-between px-4 h-16">
        <Link to="/" className="flex-shrink-0 relative z-20 self-stretch flex items-center">
          <div className="bg-background rounded-b-xl shadow-[0_8px_20px_-4px_rgba(0,0,0,0.15)] border border-t-0 border-border/40 px-4 py-3 relative" style={{ marginBottom: '-16px' }}>
            <img src={logo} alt="Nardy Automation" className="h-9 w-auto object-contain" width={120} height={36} />
          </div>
        </Link>
        <form onSubmit={handleSearch} className="flex-1 mx-4 max-w-md">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              placeholder="Search a part number..."
              className="w-full border border-border rounded-md py-2 pl-3 pr-9 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40"
              autoComplete="off"
            />
            <button type="submit" className="absolute right-0 top-0 bottom-0 px-2.5 text-muted-foreground">
              <Search className="w-4 h-4" />
            </button>
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
                <SuggestionsDropdown />
              </div>
            )}
          </div>
        </form>
        <div className="flex items-center gap-3">
          <button
            onClick={scrollToQuoteForm}
            className="flex items-center gap-1.5 bg-brand text-white px-4 py-2 rounded-md text-xs font-bold"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Quote
          </button>
          <button onClick={() => setMenuOpen(!menuOpen)} className="text-foreground/80 p-1" aria-label="Toggle menu">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* ── Mobile: Top Bar (Dark) ── */}
      <div className="md:hidden bg-[hsl(220,15%,13%)] px-3 py-2">
        <p className="text-white text-[11px] font-semibold leading-snug text-center">
          Same Day Shipping &amp; Rush Processing Available.
        </p>
        <div className="flex items-center justify-center gap-1.5 mt-0.5 flex-wrap">
          <a href="tel:+18454533201" className="flex items-center gap-1 text-white text-[11px] font-semibold hover:underline whitespace-nowrap">
            <Phone className="w-3 h-3 flex-shrink-0" />
            +1 (845) 453-3201
          </a>
          <span className="text-white/80 text-[11px] leading-snug text-center">Experts Available 24/7 365</span>
        </div>
      </div>

      {/* ── Mobile: Header Row ── */}
      <div className="md:hidden bg-background">
        <div className="flex items-center justify-between px-4 h-14">
          <button onClick={() => setMenuOpen(!menuOpen)} className="text-foreground p-1" aria-label="Toggle menu">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          
          <Link to="/" className="flex-shrink-0">
            <img src={logo} alt="Nardy Automation" className="h-9 w-auto object-contain" width={120} height={36} />
          </Link>
          
          <div className="flex items-center gap-3">
            <Link to="/auth" className="text-foreground p-1" aria-label="My Account">
              <User className="w-6 h-6" />
            </Link>
            <Suspense fallback={null}><CartIconMobile /></Suspense>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="px-4 pb-3" ref={searchMobileRef}>
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              placeholder="Keyword / Part Number / SKU"
              className="w-full border border-border rounded-full py-2.5 pl-4 pr-24 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40"
              autoComplete="off"
            />
            <button 
              type="submit" 
              className="absolute right-1 top-1 bottom-1 px-5 bg-brand text-white text-sm font-semibold rounded-full hover:bg-brand/90 transition-colors"
            >
              Search
            </button>
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
                <SuggestionsDropdown />
              </div>
            )}
          </form>
        </div>
      </div>

      {/* ── Mobile / Tablet dropdown menu ── */}
      {menuOpen && (
        <div className="lg:hidden border-b border-border bg-background px-4 py-4 space-y-3 shadow-lg">
          <Link to="/" onClick={() => setMenuOpen(false)} className="block text-foreground/80 font-medium py-1.5 hover:text-brand transition-colors">Home</Link>
          <Link to="/shop" onClick={() => setMenuOpen(false)} className="block text-foreground/80 font-medium py-1.5 hover:text-brand transition-colors">Shop</Link>
          <Link to="/categories" onClick={() => setMenuOpen(false)} className="block text-foreground/80 font-medium py-1.5 hover:text-brand transition-colors">Categories</Link>

          {/* Transformer Parts accordion */}
          <div>
            <button
              type="button"
              onClick={() => setMobilePartsOpen(!mobilePartsOpen)}
              className="flex items-center justify-between w-full text-foreground/80 font-medium py-1.5 hover:text-brand transition-colors"
            >
              Transformer Parts
              <ChevronDown className={`w-4 h-4 transition-transform ${mobilePartsOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobilePartsOpen && (
              <div className="pl-4 mt-1 space-y-1.5 border-l-2 border-brand/30">
                <Link to="/transformer-parts" onClick={() => setMenuOpen(false)} className="block text-brand text-sm font-semibold py-1">
                  All Transformer Parts →
                </Link>
                {transformerPartsLinks.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} className="block text-foreground/70 text-sm py-1 hover:text-brand transition-colors">
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/transformer-services" onClick={() => setMenuOpen(false)} className="block text-foreground/80 font-medium py-1.5 hover:text-brand transition-colors">Transformer Services</Link>
          <Link to="/industrial-automation-parts" onClick={() => setMenuOpen(false)} className="block text-foreground/80 font-medium py-1.5 hover:text-brand transition-colors">Industrial Automation Parts</Link>
          <Link to="/manufacturers" onClick={() => setMenuOpen(false)} className="block text-foreground/80 font-medium py-1.5 hover:text-brand transition-colors">Manufacturers</Link>
          <Link to="/about" onClick={() => setMenuOpen(false)} className="block text-foreground/80 font-medium py-1.5 hover:text-brand transition-colors">About Us</Link>
          <Link to="/contact" onClick={() => setMenuOpen(false)} className="block text-foreground/80 font-medium py-1.5 hover:text-brand transition-colors">Contact Us</Link>
          <Link to="/request-quote" onClick={() => setMenuOpen(false)} className="block bg-brand text-white font-semibold py-2 px-3 rounded-md text-center hover:bg-brand/90 transition-colors">Request a Quote</Link>
          <div className="border-t border-border pt-3 space-y-2 text-sm text-foreground/70">
            <a href="tel:+18454533201" className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-brand" /> +1 (845) 453-3201
            </a>
            <a href="mailto:sales@nardyautomation.com" className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-brand" /> <span translate="no">sales@nardyautomation.com</span>
            </a>
            <a href="https://wa.me/18454533201" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-brand transition-colors">
              <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
              WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;
